import React, { useEffect, useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  Typography,
  IconButton,
} from "@mui/material";
import { Add, Remove } from "@mui/icons-material";
import { useAddProgramMutation, useUpdateProgramMutation } from "../api/programsApi";
import { Program, ProgramFormData } from "../types/program";

interface Props {
  open: boolean;
  setModalOpen: (open: boolean) => void;
  onSuccess?: () => void;
  program?: Program | null;
}

const AddEditProgram: React.FC<Props> = ({ open, setModalOpen, onSuccess, program }) => {
  const [formData, setFormData] = useState<ProgramFormData>({
    name: "",
    description: "",
    objectives: [""],
    achievements: [""],
  });

  const [addProgram] = useAddProgramMutation();
  const [updateProgram] = useUpdateProgramMutation();
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (program) {
      setFormData({
        name: program.name || "",
        description: program.description || "",
        objectives: program.objectives?.map((obj) => obj.program_objective) || [""],
        achievements: program.achievements?.map((ach) => ach.program_achievement) || [""],
      });
    } else {
      setFormData({
        name: "",
        description: "",
        objectives: [""],
        achievements: [""],
      });
    }
  }, [program]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleArrayChange = (field: "objectives" | "achievements", index: number, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].map((item, i) => (i === index ? value : item)),
    }));
  };

  const addArrayItem = (field: "objectives" | "achievements") => {
    setFormData((prev) => ({
      ...prev,
      [field]: [...prev[field], ""],
    }));
  };

  const removeArrayItem = (field: "objectives" | "achievements", index: number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = useCallback(async () => {
    if (!formData.name.trim()) {
      setSubmissionError("Name is required");
      return;
    }

    setIsSaving(true);
    setSubmissionError(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name);
      formDataToSend.append("description", formData.description);

      // Add objectives
      formData.objectives.forEach((objective, index) => {
        if (objective.trim()) {
          formDataToSend.append(`objectives[${index}][program_objective]`, objective);
        }
      });

      // Add achievements
      formData.achievements.forEach((achievement, index) => {
        if (achievement.trim()) {
          formDataToSend.append(`achievements[${index}][program_achievement]`, achievement);
        }
      });

      if (program) {
        await updateProgram({
          id: program.id.toString(),
          body: formDataToSend,
        }).unwrap();
      } else {
        await addProgram({
          body: formDataToSend,
        }).unwrap();
      }

      setModalOpen(false);
      onSuccess?.();
    } catch (error: any) {
      setSubmissionError(error?.data?.message || "An error occurred while saving the program");
    } finally {
      setIsSaving(false);
    }
  }, [formData, program, addProgram, updateProgram, setModalOpen, onSuccess]);

  const handleClose = () => {
    setModalOpen(false);
    setSubmissionError(null);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{program ? "Edit Program" : "Add New Program"}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          {submissionError && <Alert severity="error">{submissionError}</Alert>}

          {/* Name */}
          <TextField
            label="Name"
            value={formData.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            fullWidth
            required
          />

          {/* Overview */}
          <TextField
            label="Overview of the pillar"
            value={formData.description}
            onChange={(e) => handleInputChange("description", e.target.value)}
            fullWidth
            multiline
            rows={3}
          />

          {/* Objectives */}
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="subtitle2">What the pillar aims to achieve</Typography>
              <IconButton size="small" onClick={() => addArrayItem("objectives")}>
                <Add />
              </IconButton>
            </Box>
            {formData.objectives.map((objective, index) => (
              <Box key={index} sx={{ display: "flex", gap: 1, mb: 1 }}>
                <TextField
                  value={objective}
                  onChange={(e) => handleArrayChange("objectives", index, e.target.value)}
                  fullWidth
                  placeholder="Enter objective"
                />
                {formData.objectives.length > 1 && (
                  <IconButton size="small" onClick={() => removeArrayItem("objectives", index)}>
                    <Remove />
                  </IconButton>
                )}
              </Box>
            ))}
          </Box>

          {/* Achievements */}
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="subtitle2">Result Highlight (Key outcomes)</Typography>
              <IconButton size="small" onClick={() => addArrayItem("achievements")}>
                <Add />
              </IconButton>
            </Box>
            {formData.achievements.map((achievement, index) => (
              <Box key={index} sx={{ display: "flex", gap: 1, mb: 1 }}>
                <TextField
                  value={achievement}
                  onChange={(e) => handleArrayChange("achievements", index, e.target.value)}
                  fullWidth
                  placeholder="Enter key outcome"
                />
                {formData.achievements.length > 1 && (
                  <IconButton size="small" onClick={() => removeArrayItem("achievements", index)}>
                    <Remove />
                  </IconButton>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={20} /> : null}
        >
          {isSaving ? "Saving..." : program ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditProgram;
